<script lang="ts">
  /*
   * App.svelte — app shell.
   *
   * Two layout modes:
   *   Edit     TOC + 50/50 editor/preview cards (Cmd/Ctrl+1, default)
   *   Preview  TOC + full-width preview card     (Cmd/Ctrl+2)
   *
   * TOC stays in BOTH modes — navigation is most valuable in Preview
   * where the entire viewport is the rendered doc. On mobile the TOC
   * hides itself via its own media query.
   *
   * Header is three affordances on the right — FileMenu (open / save /
   * share / export / copy / clear), theme toggle, Settings. No
   * standalone icon buttons, no manual resize handle. Clear is undoable
   * via toast action; no confirm dialog for it.
   *
   * Two-card minimalism: editor and preview each sit as cards on a
   * single page sheet. Header + TOC sit transparent on that sheet — only
   * the two content panes carry surface chrome (radius, border, shadow).
   *
   * Keyboard shortcuts use e.preventDefault() because browsers bind ⌘1/⌘2
   * to tab switching. Listener registers in onMount for the app lifetime.
   */
  import { onMount, tick } from 'svelte'
  import { Settings as SettingsIcon, Sun, Moon, Search, ChevronUp, ChevronDown, X } from '@lucide/svelte'
  import CommandPalette from './components/CommandPalette.svelte'
  import EmptyState from './components/EmptyState.svelte'
  import FileMenu from './components/FileMenu.svelte'
  import PaneToolbar from './components/PaneToolbar.svelte'
  import Preview from './components/Preview.svelte'
  import SettingsPanel from './components/SettingsPanel.svelte'
  import ShareDialog from './components/ShareDialog.svelte'
  import Toc from './components/Toc.svelte'
  import ToastHost from './components/ToastHost.svelte'
  import { escapeRegex, highlightMatches, unwrapSearchMarks } from './lib/previewHighlight'
  import { formatShortcut } from './lib/platform'

  // Editor is lazy — CodeMirror is ~500 KB gzipped and the Read-mode wedge
  // (paste markdown, get a polished render) doesn't need it. We trigger the
  // import on app boot AND eagerly the first time we render write/review,
  // so by the time the user switches modes the chunk is usually already
  // in flight.
  type EditorMod = typeof import('./components/Editor.svelte')
  let editorPromise: Promise<EditorMod> | null = null
  function loadEditor(): Promise<EditorMod> {
    if (!editorPromise) editorPromise = import('./components/Editor.svelte')
    return editorPromise
  }
  import type { Command } from './lib/commands'
  import type { TocNode } from './lib/render/toc'
  import type { RenderResult } from './lib/render'
  import { trackActiveHeading } from './lib/activeHeading'
  import { bindDragDrop } from './lib/dragDrop'
  import { saveRecent, listRecents, type Recent } from './lib/recents'
  import { readShareFromUrl } from './lib/share'
  import { settings, type LayoutMode } from './state/settings.svelte'
  import { WELCOME } from './samples/welcome'
  import { nextStop, prevStop } from './lib/zoom'
  import { safeGetItem, safeSetItem } from './lib/safeStorage'
  import { getDocTitle } from './lib/render/title'
  import { openMarkdownFile, saveMarkdownFile } from './lib/fileAccess'
  import { showToast } from './state/toast.svelte'

  // `settings` is now a reactive Svelte 5 $state singleton imported above —
  // no constructor needed. Mutate fields directly; effects below + UI bindings
  // pick up changes automatically; persistence happens via the module's own
  // $effect.root.

  // Source — restore from last session or seed with WELCOME for first run.
  // localStorage is the right home for V1: instant, no auth, no async. The
  // IndexedDB recents store (Phase 11) lives alongside this, not in place
  // of it — `md.content` is "current document", recents are "history".
  // safeGetItem swallows any storage exceptions (private mode, quota, etc)
  // and returns null — the app falls back to WELCOME just like a first run.
  let source = $state(safeGetItem('md.content') ?? WELCOME)

  // ---- ephemeral share session (audit 2026-05-23 P0 #2) ----
  //
  // When the URL carries `#s=<payload>`, the recipient is VIEWING someone
  // else's document. They should not have their own work overwritten:
  //   - md.content (localStorage) must keep the recipient's own draft
  //   - layoutMode (Settings) must keep the recipient's preferred default
  // `isSharedSession` flips both persistence effects below to no-op until
  // the recipient starts editing. The FIRST edit (i.e. `source` diverges
  // from the loaded shared value) flips the flag back to false and lets
  // normal persistence resume — the user has chosen to take over the
  // document so it's now theirs to keep.
  //
  // `sharedSourceSnapshot` is captured in onMount BEFORE we replace
  // `source`, so the equality check below stays cheap (string identity
  // is enough; the lz-string decode produces a fresh string per load).
  let isSharedSession = $state(false)
  let sharedSourceSnapshot: string | null = null

  $effect(() => {
    // Persist on every keystroke. localStorage.setItem is sync but fast
    // enough on documents at the 64KB share-cap; if it ever shows up in
    // a profile, debounce here (don't move to async storage).
    //
    // SHARE-SESSION GATE: while viewing a shared payload we must not
    // overwrite the recipient's own draft. The moment they edit (source
    // diverges from the snapshot we loaded) we flip the flag and resume
    // normal persistence.
    if (isSharedSession) {
      if (sharedSourceSnapshot !== null && source !== sharedSourceSnapshot) {
        isSharedSession = false
        sharedSourceSnapshot = null
      } else {
        return
      }
    }
    // safeSetItem swallows quota / private-mode failures. The session
    // still round-trips via the in-memory fallback inside safeStorage —
    // a hard reload would lose work but typing within the tab stays
    // coherent.
    safeSetItem('md.content', source)
  })

  // ---- IndexedDB recents ----
  //
  // 8-second debounced autosave into the recents store. The localStorage
  // persist above handles "current document"; this effect handles "history"
  // — when the user pauses typing for 8s we snapshot whatever is on screen
  // into IndexedDB and refresh recentsList so the EmptyState surface +
  // command palette pick up the new entry. (The earlier 30s window
  // was long enough that most edit-then-clear sessions never produced
  // a recent at all, which made the EmptyState's "Recent" list feel
  // permanently empty.)
  //
  // Title comes from the first ATX/setext heading (best signal) or the
  // first 60 chars of the source as a fallback.
  //
  // Plain `let` (not $state) — reading saveTimer inside this effect AND
  // writing it on the next line would create an effect_update_depth_exceeded
  // loop. The timer handle isn't reactive UI state; it's a private cleanup ref.
  let saveTimer: number | null = null

  async function commitRecent(s: string): Promise<void> {
    let title = getDocTitle(s)
    if (title === 'Untitled') {
      title = s.slice(0, 60).trim() || 'Untitled'
    }
    const result = await saveRecent({ title, source: s, size: s.length }).catch(() => null)
    if (result && result.ok) {
      recentsList = await listRecents()
    }
  }

  $effect(() => {
    const s = source
    if (saveTimer) clearTimeout(saveTimer)
    if (!s.trim()) return
    saveTimer = window.setTimeout(() => {
      void commitRecent(s)
    }, 8_000)
    return () => {
      if (saveTimer) clearTimeout(saveTimer)
    }
  })

  // ---- document title (Phase 13) ----
  //
  // The first H1 in the source becomes the export filename root + the
  // <title> in exported HTML. Falls back to "Untitled" when the doc has
  // no level-1 heading. Single source of truth (P1.3 from the
  // 2026-05-23 codex audit) — `getDocTitle` recognises both ATX
  // `# Title` AND setext `Title\n=====`; the meta.title in render/index.ts
  // calls the SAME function so exports and the masthead never disagree.
  let docTitle = $derived.by(() => getDocTitle(source))

  // ---- layout mode + custom split ----

  // ---- share dialog (Phase 12) ----
  let shareOpen = $state(false)

  // ---- command palette (Phase 14) ----
  //
  // Single source of truth for every keyboard-reachable action. The palette
  // reads this list, fuzzyFilter()s by query, and runs the selected entry on
  // Enter. Anything we want users to discover via ⌘K lands here — additions
  // in later phases (recents, preset switching) extend this array, no other
  // wiring needed.
  let paletteOpen = $state(false)
  let recentsList = $state<Recent[]>([])

  // ---- global search (header) ----
  //
  // One input drives EVERYTHING search-related:
  //   1. filters the TOC (passed to <Toc filter={searchQuery} />)
  //   2. highlights live matches inside the preview body (effect below)
  //   3. exposes prev/next match navigation via the chevron buttons
  //
  // This replaced the older split (TOC's own heading-only filter + ⌘F
  // overlay anchored to the preview). The split was confusing — two
  // different searches doing two different jobs — and the TOC filter
  // only matched heading text, which made users think doc-wide search
  // was broken. Header search is global: it walks the rendered preview's
  // text nodes via lib/previewHighlight.ts and wraps matches in
  // <mark data-search>.
  let searchQuery = $state('')
  let searchMatchCount = $state(0)
  let searchCurrent = $state(0)
  // The header search input is bound here so ⌘F (and the ⌘F-yields-to-CM
  // logic in Edit mode) can focus it directly.
  let searchInputEl = $state<HTMLInputElement | undefined>()

  // ---- settings drawer (Phase 18) ----
  //
  // Right-rail drawer for theme/font/measure/line-height. The Settings
  // class persists every setter to localStorage and the App effects below
  // push the changes onto document.documentElement, so the panel itself
  // does nothing more than mutate `settings` via `bind:group`. ⌘, opens
  // the drawer (matches the macOS Preferences convention); palette entry
  // gives discoverability for Linux/Win users who lack the muscle memory.
  let settingsOpen = $state(false)

  let mode = $state<LayoutMode>(settings.layoutMode)

  $effect(() => {
    // SHARE-SESSION GATE (audit 2026-05-23 P0 #2): when viewing a shared
    // payload the readShareFromUrl path forces mode='read' on the
    // recipient's screen. Persisting that to Settings would clobber their
    // preferred default (e.g. Write). Skip the write while ephemeral —
    // once they edit, isSharedSession flips off and the next mode change
    // persists normally.
    if (isSharedSession) return
    settings.layoutMode = mode
  })

  // Grid template — Read collapses to a single column. Write is a fixed
  // 50/50 split (the manual resize handle was dropped in the 2026-05-24
  // simplification — almost nobody used it and the a11y / pointer-drag
  // surface wasn't worth the maintenance).
  const columns = $derived(mode === 'read' ? '1fr' : '1fr 1fr')

  function setMode(next: LayoutMode): void {
    mode = next
  }

  function toggleTheme(): void {
    settings.mode = resolvedMode === 'dark' ? 'light' : 'dark'
  }

  function clearDoc(): void {
    if (!source) return
    const prev = source
    // Snapshot the just-cleared draft into recents BEFORE wiping it, so
    // the EmptyState's Recent list shows something — otherwise users who
    // clear before the 8s autosave window never see their own history.
    if (prev.trim()) void commitRecent(prev)
    source = ''
    showToast('Document cleared', {
      action: {
        label: 'Undo',
        onAction: () => {
          source = prev
        },
      },
    })
  }

  function copyMarkdown(): void {
    if (!source.trim()) {
      showToast('Nothing to copy')
      return
    }
    void navigator.clipboard.writeText(source).then(() => showToast('Markdown copied'))
  }

  function loadSource(text: string): void {
    source = text
  }

  // ---- search wiring ----
  //
  // The highlight effect re-runs on every keystroke AND on source changes
  // (Preview replaces innerHTML when source changes, which wipes our
  // marks — re-applying after a `tick()` lets Preview land the new HTML
  // first). We track `lastQuery` outside the effect closure so we only
  // scrollIntoView when the QUERY changed (typed/cleared), not when the
  // source changed under us — otherwise typing in the editor would yank
  // the preview to the first match on every keystroke.
  let lastQuery = ''

  // setActiveMark — stamp data-active on exactly one mark so CSS can show
  // a stronger highlight for the current search match. With 10+ matches
  // on screen, the soft accent tint on every mark made it impossible to
  // tell where Enter/next was about to land (the user feedback that
  // prompted this). The active mark also scrolls into view.
  function setActiveMark(idx: number, opts: { scroll?: boolean } = {}): void {
    if (!previewHost) return
    const marks = previewHost.querySelectorAll<HTMLElement>('mark[data-search]')
    marks.forEach((m) => m.removeAttribute('data-active'))
    const target = marks[idx - 1]
    if (!target) return
    target.setAttribute('data-active', '')
    if (opts.scroll !== false) {
      target.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }

  $effect(() => {
    if (!previewHost) return
    // Subscribe to source so we re-highlight after Preview re-renders.
    void source
    const q = searchQuery.trim()
    const isFreshQuery = q !== lastQuery
    lastQuery = q
    void tick().then(() => {
      if (!previewHost) return
      unwrapSearchMarks(previewHost)
      if (!q) {
        searchMatchCount = 0
        searchCurrent = 0
        return
      }
      const re = new RegExp(`(${escapeRegex(q)})`, 'gi')
      const marks = highlightMatches(previewHost, re)
      searchMatchCount = marks.length
      if (isFreshQuery) {
        // Fresh query: jump to the first match (scrollIntoView).
        searchCurrent = marks.length > 0 ? 1 : 0
        setActiveMark(searchCurrent)
      } else {
        // Same query, source changed under us (user is editing): keep the
        // active position if still in range, but don't scroll — yanking the
        // preview around per keystroke is jarring. Stamp the active marker
        // back on (the unwrap above stripped it along with the marks).
        if (searchCurrent > marks.length) {
          searchCurrent = marks.length > 0 ? 1 : 0
        }
        setActiveMark(searchCurrent, { scroll: false })
      }
    })
  })

  function gotoMatch(dir: 1 | -1): void {
    if (!previewHost || searchMatchCount === 0) return
    searchCurrent = ((searchCurrent - 1 + dir + searchMatchCount) % searchMatchCount) + 1
    setActiveMark(searchCurrent)
  }

  function clearSearch(): void {
    searchQuery = ''
    searchInputEl?.blur()
  }

  function focusSearch(): void {
    searchInputEl?.focus()
    searchInputEl?.select()
  }

  function onSearchKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.preventDefault()
      clearSearch()
    } else if (e.key === 'Enter') {
      e.preventDefault()
      gotoMatch(e.shiftKey ? -1 : 1)
    }
  }

  // ---- mobile pane-at-a-time + swipe (Phase 17) ----
  //
  // `isTouch` gates swipe handlers on narrow touch devices. Swipe: 60px
  // horizontal threshold. Left swipe from Edit → Preview; right swipe back.
  let isTouch = $state(false)
  // Plain let (not $state) — passed between two event handlers only, no UI binding.
  let touchStartX: number | null = null

  function onTouchStart(e: TouchEvent): void {
    touchStartX = e.touches[0]?.clientX ?? null
  }
  function onTouchEnd(e: TouchEvent): void {
    if (touchStartX === null) return
    const endX = e.changedTouches[0]?.clientX ?? touchStartX
    const dx = endX - touchStartX
    touchStartX = null
    if (Math.abs(dx) < 60) return
    if (mode === 'write' && dx < 0) mode = 'read'
    else if (mode === 'read' && dx > 0) mode = 'write'
  }

  // ---- registered commands (Phase 14) ----
  //
  // $derived so the closures capture the current `settings`, `mode`,
  // `shareOpen`, and `source` reactive values rather than stale snapshots.
  // Shortcut hints are spelled "Ctrl+…" for universality; the actual binding
  // checks both metaKey (Mac) and ctrlKey (Win/Linux) in onKey below.
  let commands = $derived<Command[]>([
    {
      id: 'file.open',
      title: 'Open file…',
      hint: 'File',
      run: async () => {
        const text = await openMarkdownFile()
        if (text !== null) source = text
      },
    },
    {
      id: 'file.save',
      title: 'Save file…',
      hint: 'File',
      shortcut: 'Ctrl+S',
      run: async () => {
        await saveMarkdownFile(source, docTitle)
      },
    },
    ...recentsList.map((r) => ({
      id: `recent.${r.id}`,
      title: `Open recent: ${r.title}`,
      hint: 'Recent',
      run: () => {
        source = r.source
      },
    })),
    {
      id: 'share',
      title: 'Share by URL',
      shortcut: 'Ctrl+Shift+S',
      run: () => {
        shareOpen = true
      },
    },
    {
      id: 'search',
      title: 'Find in document',
      shortcut: 'Ctrl+F',
      run: () => {
        focusSearch()
      },
    },
    {
      id: 'settings',
      title: 'Open settings',
      shortcut: 'Ctrl+,',
      run: () => {
        settingsOpen = true
      },
    },
    {
      id: 'pin',
      title: 'Pin this document',
      hint: 'Recent',
      run: async () => {
        if (!source.trim()) return
        let title = getDocTitle(source)
        if (title === 'Untitled') title = source.slice(0, 60).trim() || 'Untitled'
        await saveRecent({ title, source, size: source.length, pinned: true })
        recentsList = await listRecents()
        showToast('Document pinned')
      },
    },
    {
      id: 'sample',
      title: 'Load sample document',
      run: () => {
        source = WELCOME
      },
    },
    {
      id: 'copy',
      title: 'Copy markdown',
      run: () => copyMarkdown(),
    },
    {
      id: 'clear',
      title: 'Clear document',
      run: () => clearDoc(),
    },
  ])

  // ---- theme + mode + typography wiring ----
  //
  // Push light/dark mode onto <html> and fixed typography tokens.

  // Resolved light/dark for UI (header icon, theme-color meta, html dataset).
  let resolvedMode = $state<'light' | 'dark'>('light')

  $effect(() => {
    let m: 'light' | 'dark' = 'light'
    if (settings.mode === 'auto') {
      m = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    } else {
      m = settings.mode
    }
    resolvedMode = m
    document.documentElement.dataset['mode'] = m
    const meta = document.getElementById('theme-color-meta') as HTMLMetaElement | null
    if (meta) meta.content = m === 'dark' ? '#0d1117' : '#f6f8fa'
  })

  $effect(() => {
    document.documentElement.style.setProperty('--measure', '720px')
    document.documentElement.style.setProperty('--line-height', '1.6')
  })

  // Live-update mode when the user changes their OS scheme while we're in
  // 'auto', and listen for Cmd/Ctrl + 1/2/3 to switch layout modes. Both
  // live in the same onMount so we only register/tear-down once.
  onMount(() => {
    // Detect touch device once at mount for swipe between Edit/Preview.
    isTouch = window.matchMedia('(pointer: coarse) and (max-width: 768px)').matches

    void listRecents().then((rows) => {
      recentsList = rows
    })

    if (isTouch && !safeGetItem('md.swipeHintSeen')) {
      showToast('Swipe to switch Edit and Preview')
      safeSetItem('md.swipeHintSeen', '1')
    }

    const warm = (): void => {
      void loadEditor()
    }
    if ('requestIdleCallback' in window) {
      ;(window as Window & { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(warm)
    } else {
      setTimeout(warm, 600)
    }

    const shared = readShareFromUrl()
    if (shared) {
      isSharedSession = true
      sharedSourceSnapshot = shared
      source = shared
      mode = 'read'
    }

    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    const onSchemeChange = (): void => {
      if (settings.mode === 'auto') {
        resolvedMode = mql.matches ? 'dark' : 'light'
        document.documentElement.dataset['mode'] = resolvedMode
        const meta = document.getElementById('theme-color-meta') as HTMLMetaElement | null
        if (meta) meta.content = resolvedMode === 'dark' ? '#0d1117' : '#f6f8fa'
      }
    }
    mql.addEventListener('change', onSchemeChange)

    const onKey = (e: KeyboardEvent): void => {
      const meta = e.metaKey || e.ctrlKey
      if (!meta) return
      // ⌘Shift+S — Share. Check shift branch BEFORE the bare-S branches so
      // the shift modifier wins; preventDefault stops browsers from invoking
      // their own Save dialog.
      if (e.shiftKey && (e.key === 'S' || e.key === 's')) {
        e.preventDefault()
        shareOpen = true
        return
      }
      if (e.key === 's' || e.key === 'S') {
        e.preventDefault()
        void saveMarkdownFile(source, docTitle)
        return
      }
      if (e.key === '1') {
        e.preventDefault()
        setMode('write')
      } else if (e.key === '2') {
        e.preventDefault()
        setMode('read')
      } else if (e.key === '=' || e.key === '+') {
        // US layouts produce '=' unshifted and '+' shifted on the same key —
        // accept both so ⌘+ feels natural either way.
        e.preventDefault()
        settings.previewZoom = nextStop(settings.previewZoom)
      } else if (e.key === '-') {
        e.preventDefault()
        settings.previewZoom = prevStop(settings.previewZoom)
      } else if (e.key === '0') {
        e.preventDefault()
        settings.previewZoom = 100
      } else if (e.key === 'k' || e.key === 'K') {
        // preventDefault is load-bearing — browser ⌘K focuses the URL bar.
        e.preventDefault()
        paletteOpen = true
      } else if ((e.key === 'f' || e.key === 'F') && !e.shiftKey) {
        // ⌘F focuses the always-visible header search input. In Edit mode
        // we yield to CodeMirror's native find so typing inside the editor
        // pane still gets its own incremental search — header search is
        // primarily for navigating the rendered preview. The "is the
        // editor focused" check uses document.activeElement so users who
        // tab/click to the search input and press ⌘F still get a focus +
        // select (re-focus is a no-op but the select shows intent).
        const active = document.activeElement as HTMLElement | null
        if (mode === 'write' && active?.closest('.cm-editor')) return
        e.preventDefault()
        focusSearch()
      } else if (e.key === ',') {
        // Phase 18: ⌘, mirrors the macOS Preferences convention. Bare
        // comma is otherwise unbound at the window level, so claiming it
        // for settings doesn't trample any browser shortcut. The shift
        // branch (⌘Shift+S → Share) already returned above, so we're safe
        // to consume comma here without bleeding into other handlers.
        e.preventDefault()
        settingsOpen = true
      }
    }
    window.addEventListener('keydown', onKey)

    // Phase 10: drop any .md/.txt onto the window to load it as the source.
    // Bound to document.body (not the preview pane) so the editor pane and
    // the chrome both accept drops — there's no aiming required.
    //
    // P1.9 from the 2026-05-23 codex audit: drops > 5MB are rejected with a
    // user-facing alert so a 200MB accidental log-file drop tells the user
    // why nothing happened instead of just locking the tab during text()
    // + the subsequent markdown render.
    const teardownDragDrop = bindDragDrop(
      document.body,
      (text) => {
        source = text
      },
      (msg) => {
        showToast(msg)
      },
    )

    // Phase 16: PWA auto-reload on update. `registerType: 'autoUpdate'`
    // in vite.config.ts pairs with the `onNeedRefresh → updateSW(true)` call
    // in main.ts; once the new SW activates, `controllerchange` fires here
    // and we location.reload() — but only if there was ALREADY a controller
    // when the page loaded. On the very first visit the SW activates for the
    // first time, controllerchange fires, and without this guard we'd bounce
    // the page during install (annoying on a brand-new PWA tab). The pattern
    // matches the tv-dev pattern (reference_pwa_auto_reload).
    let hadController = !!navigator.serviceWorker?.controller
    const onControllerChange = (): void => {
      if (hadController) location.reload()
      hadController = true
    }
    navigator.serviceWorker?.addEventListener(
      'controllerchange',
      onControllerChange,
    )

    return () => {
      mql.removeEventListener('change', onSchemeChange)
      window.removeEventListener('keydown', onKey)
      teardownDragDrop()
      navigator.serviceWorker?.removeEventListener(
        'controllerchange',
        onControllerChange,
      )
    }
  })

  function handleSourceChange(v: string): void {
    source = v
  }

  // ---- TOC sidebar + active-heading sync ----

  let toc = $state<TocNode[]>([])
  let activeId = $state<string | null>(null)
  let previewHost = $state<HTMLElement | undefined>()
  let previewScroller = $state<HTMLElement | undefined>()

  function onPreviewRender(result: RenderResult): void {
    toc = result.toc
  }

  function jumpTo(id: string): void {
    // CSS.escape is essential — heading IDs are slugified text and can
    // legally contain hyphens, digits-leading sequences, and any other
    // char that has meaning in a CSS selector.
    const el = previewHost?.querySelector(`#${CSS.escape(id)}`) as HTMLElement | null
    if (!el) return
    el.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    })
    // SHARE-SESSION GATE (audit 2026-05-23 P0 #3): when viewing a shared
    // document the hash carries `#s=<lz-payload>`. Replacing it with
    // `#<heading-id>` would destroy the share URL — refresh/back/copy-link
    // would no longer reproduce the document. Scroll only; the active
    // heading is still tracked via trackActiveHeading + activeId state, so
    // the TOC UI updates correctly without history mutation.
    if (isSharedSession) return
    // replaceState (not pushState) — clicking through the TOC shouldn't
    // pollute the browser history with one entry per heading.
    history.replaceState(null, '', `#${id}`)
  }

  $effect(() => {
    if (!previewScroller) return
    return trackActiveHeading(previewScroller, (id) => (activeId = id))
  })
</script>

<svelte:head>
  <title>md</title>
</svelte:head>

<div class="app" data-mode-active={mode}>
  <header class="app-header">
    <div class="header-left">
      <img class="brand-logo" src="/md/md-logo.png" alt="" width="22" height="22" />
      <span class="brand">md</span>
      {#if isSharedSession}
        <span class="badge">Shared</span>
      {/if}
    </div>

    <!-- Global search — the single search affordance for the whole app.
         Filters the TOC AND highlights matches in the preview body
         simultaneously. ⌘F focuses this input; Enter/Shift+Enter cycle
         through matches; Esc clears. Replaced the older split of
         "TOC's own filter input (headings only)" + "⌘F overlay above
         the preview pane". -->
    <div class="search-wrap" role="search">
      <Search size={15} strokeWidth={2} class="search-icon" aria-hidden="true" />
      <input
        bind:this={searchInputEl}
        bind:value={searchQuery}
        type="search"
        class="search-input"
        placeholder="Search in document…"
        aria-label="Search in document"
        onkeydown={onSearchKey}
      />
      {#if searchQuery}
        <span class="search-count" aria-live="polite">
          {searchMatchCount === 0 ? '0' : `${searchCurrent} / ${searchMatchCount}`}
        </span>
        <button
          type="button"
          class="search-nav"
          title={`Previous match (${formatShortcut('Shift+Enter')})`}
          aria-label="Previous match"
          disabled={searchMatchCount === 0}
          onclick={() => gotoMatch(-1)}
        >
          <ChevronUp size={14} strokeWidth={2.25} aria-hidden="true" />
        </button>
        <button
          type="button"
          class="search-nav"
          title="Next match (Enter)"
          aria-label="Next match"
          disabled={searchMatchCount === 0}
          onclick={() => gotoMatch(1)}
        >
          <ChevronDown size={14} strokeWidth={2.25} aria-hidden="true" />
        </button>
        <button
          type="button"
          class="search-nav"
          title="Clear (Esc)"
          aria-label="Clear search"
          onclick={clearSearch}
        >
          <X size={14} strokeWidth={2.25} aria-hidden="true" />
        </button>
      {/if}
    </div>

    <div class="header-right">
      <div class="segmented" role="group" aria-label="View">
        <button
          type="button"
          class="seg-btn"
          class:active={mode === 'write'}
          aria-pressed={mode === 'write'}
          title={`Edit (${formatShortcut('Ctrl+1')})`}
          onclick={() => setMode('write')}
        >Edit</button>
        <button
          type="button"
          class="seg-btn"
          class:active={mode === 'read'}
          aria-pressed={mode === 'read'}
          title={`Preview (${formatShortcut('Ctrl+2')})`}
          onclick={() => setMode('read')}
        >Preview</button>
      </div>
      <FileMenu
        {source}
        title={docTitle}
        recents={recentsList}
        onLoadSource={loadSource}
        onOpenRecent={(text) => (source = text)}
        onShare={() => (shareOpen = true)}
        onCopy={copyMarkdown}
        onClear={clearDoc}
      />
      <button
        type="button"
        class="btn-icon"
        title={resolvedMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        aria-label="Toggle light/dark theme"
        onclick={toggleTheme}
      >
        {#if resolvedMode === 'dark'}
          <Sun size={18} strokeWidth={1.75} aria-hidden="true" />
        {:else}
          <Moon size={18} strokeWidth={1.75} aria-hidden="true" />
        {/if}
      </button>
      <button
        type="button"
        class="btn-icon"
        title={`Settings (${formatShortcut('Ctrl+,')})`}
        aria-label="Settings"
        onclick={() => (settingsOpen = true)}
      >
        <SettingsIcon size={18} strokeWidth={1.75} aria-hidden="true" />
      </button>
    </div>
  </header>

  {#if shareOpen}
    <ShareDialog {source} onClose={() => (shareOpen = false)} />
  {/if}

  {#if settingsOpen}
    <SettingsPanel onClose={() => (settingsOpen = false)} />
  {/if}

  {#if paletteOpen}
    <CommandPalette {commands} onClose={() => (paletteOpen = false)} />
  {/if}

  <main
    class="app-main"
    style="grid-template-columns: auto {columns};"
    ontouchstart={isTouch ? onTouchStart : undefined}
    ontouchend={isTouch ? onTouchEnd : undefined}
  >
    <!-- TOC stays in both Edit and Read modes (rendered here once, ahead
         of both panes). The whole point of a TOC is navigation, and Read
         mode is where navigation matters most — you're looking at the
         rendered doc full-width and you want one click to any heading.
         The Toc.svelte CSS already hides itself on coarse-pointer
         mobile, so this doesn't crowd small screens.

         `filter` ties the TOC's visibility to the SAME header search
         query that drives the preview highlight effect — one search,
         both surfaces. -->
    <Toc nodes={toc} {activeId} onJump={jumpTo} filter={searchQuery} />
    {#if mode !== 'read'}
      <section class="pane editor-pane">
        {#await loadEditor()}
          <div class="editor-loading" aria-hidden="true">
            <span class="spinner"></span>
          </div>
        {:then mod}
          {@const EditorComp = mod.default}
          <EditorComp value={source} onChange={handleSourceChange} />
        {/await}
      </section>
    {/if}
    <section class="pane preview-pane">
      {#if source.trim() === ''}
        <EmptyState
          onLoadSample={() => (source = WELCOME)}
          recents={recentsList}
          onOpenRecent={(text) => (source = text)}
        />
      {:else}
        <Preview
          source={source}
          zoom={settings.previewZoom}
          wide={mode === 'read'}
          bind:host={previewHost}
          bind:scroller={previewScroller}
          onRender={onPreviewRender}
        />
        <PaneToolbar onCopy={copyMarkdown} onClear={clearDoc} />
      {/if}
    </section>
  </main>

  <ToastHost />
</div>

<style>
  :global(html, body) {
    margin: 0;
    padding: 0;
    height: 100%;
    width: 100%;
    overflow: hidden;
    background: var(--bg-page);
    color: var(--text-primary);
    font-family: var(--font-body);
    -webkit-font-smoothing: antialiased;
  }

  :global(*) {
    box-sizing: border-box;
  }

  :global(body.drag-over) {
    outline: 2px dashed var(--accent);
    outline-offset: -4px;
  }

  .app {
    display: grid;
    grid-template-rows: auto 1fr;
    height: 100dvh;
    width: 100%;
    max-width: 100vw;
    overflow: hidden;
    background: var(--bg-page);
  }

  /* Header sits transparent directly on the page sheet — no panel chrome.
     The two-card minimalism (editor card + preview card on a plain sheet)
     means everything except those two surfaces must blend into the bg.

     Layout: brand on the left, global search expanding in the center,
     action cluster on the right (segmented + FileMenu + theme + Settings). */
  .app-header {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-2) var(--space-3);
    min-height: 44px;
    background: transparent;
    flex-shrink: 0;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    min-width: 0;
    justify-self: start;
  }

  .brand-logo {
    display: block;
    flex-shrink: 0;
    border-radius: 6px;
    background: #ffffff;
    padding: 3px;
    box-sizing: content-box;
    border: 1px solid var(--border-subtle);
  }

  .brand {
    font-size: var(--text-lg);
    font-weight: 700;
    color: var(--text-primary);
  }

  .badge {
    font-size: var(--text-xs);
    font-weight: 600;
    color: var(--accent);
    background: var(--accent-soft);
    padding: 2px 8px;
    border-radius: var(--radius-pill);
  }

  /* Global search input — pill-shaped, surface-toned so it pops on the
     otherwise-transparent header sheet. Centered in the header and
     constrained to a max width so it doesn't get absurdly wide on
     ultrawide displays. */
  .search-wrap {
    display: flex;
    align-items: center;
    gap: 4px;
    width: 100%;
    max-width: 520px;
    justify-self: center;
    padding: 4px 8px 4px 10px;
    background: var(--bg-surface);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-pill);
    transition: border-color var(--motion-fast), box-shadow var(--motion-fast);
  }
  .search-wrap:focus-within {
    border-color: var(--accent);
    box-shadow: var(--shadow-ring);
  }
  .search-wrap :global(.search-icon) {
    color: var(--text-muted);
    flex-shrink: 0;
  }
  .search-input {
    flex: 1;
    min-width: 0;
    border: none;
    padding: 4px 4px;
    background: transparent;
    color: var(--text-primary);
    font: inherit;
    font-size: var(--text-sm);
    outline: none;
  }
  .search-input::placeholder {
    color: var(--text-muted);
  }
  /* Hide the native search clear (the ✕) — we provide our own. */
  .search-input::-webkit-search-cancel-button {
    -webkit-appearance: none;
    display: none;
  }
  .search-count {
    font-size: var(--text-xs);
    color: var(--text-muted);
    padding: 0 6px;
    font-variant-numeric: tabular-nums;
    min-width: 44px;
    text-align: center;
    border-left: 1px solid var(--border-subtle);
    flex-shrink: 0;
  }
  .search-nav {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    border-radius: var(--radius-pill);
    background: transparent;
    color: var(--text-secondary);
    cursor: pointer;
    flex-shrink: 0;
  }
  .search-nav:hover:not(:disabled) {
    background: var(--bg-sunken);
    color: var(--text-primary);
  }
  .search-nav:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .search-nav:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }

  .segmented {
    display: inline-flex;
    padding: 2px;
    background: var(--bg-sunken);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-md);
  }

  .seg-btn {
    appearance: none;
    border: none;
    background: transparent;
    color: var(--text-secondary);
    font: inherit;
    font-size: var(--text-sm);
    font-weight: 500;
    padding: 6px 14px;
    border-radius: calc(var(--radius-md) - 2px);
    cursor: pointer;
  }
  .seg-btn:hover {
    color: var(--text-primary);
  }
  .seg-btn.active {
    background: var(--bg-surface);
    color: var(--text-primary);
    box-shadow: var(--shadow-sm);
  }
  .seg-btn:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: var(--space-1);
    justify-self: end;
  }

  .btn-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: var(--radius-sm);
    background: transparent;
    color: var(--text-secondary);
    cursor: pointer;
  }
  .btn-icon:hover {
    color: var(--text-primary);
    background: var(--bg-surface);
  }
  .btn-icon:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }

  /* Main canvas is the sheet. We pad it on three sides so the editor +
     preview cards float with a margin, and use grid `gap` instead of a
     pane divider so the breathing room between cards IS the separator. */
  .app-main {
    display: grid;
    min-height: 0;
    min-width: 0;
    width: 100%;
    overflow: hidden;
    background: transparent;
    padding: 0 var(--space-3) var(--space-3);
    gap: var(--space-3);
  }

  /* Panes become cards on the sheet — bg-surface, subtle border, rounded
     corners with overflow:hidden so the inner scroll containers
     (CodeMirror, the preview scroller, the floating PaneToolbar) all clip
     to the rounded shape. */
  .pane {
    min-height: 0;
    min-width: 0;
    background: var(--bg-surface);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-sm);
    overflow: hidden;
  }

  .editor-loading {
    height: 100%;
    display: grid;
    place-items: center;
  }
  .editor-loading .spinner {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2px solid var(--border-subtle);
    border-top-color: var(--accent);
    animation: spin 750ms linear infinite;
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .preview-pane {
    position: relative;
  }

  /* Header search highlight inside the rendered preview. Two tiers:
       - All matches get a soft accent tint so users see the density of
         hits at a glance.
       - The CURRENTLY-FOCUSED match (the one Enter / next-button will
         jump from) gets a solid accent fill + outline ring so it stands
         out unmistakably even when there are 10+ matches on screen.
     The mark wrapper is set up by lib/previewHighlight.ts; the
     data-active attribute is toggled by setActiveMark in App.svelte. */
  :global(.prose mark[data-search]) {
    background: color-mix(in srgb, var(--accent) 22%, transparent);
    color: var(--text-primary);
    padding: 0 2px;
    border-radius: 3px;
    transition: background var(--motion-fast), box-shadow var(--motion-fast);
  }
  :global(.prose mark[data-search][data-active]) {
    background: var(--accent);
    color: var(--accent-contrast);
    box-shadow:
      0 0 0 2px var(--accent),
      0 0 0 4px color-mix(in srgb, var(--accent) 35%, transparent);
    font-weight: 600;
  }
  :global(.prose mark[data-search][data-active] code) {
    color: inherit;
    background: color-mix(in srgb, var(--accent-contrast) 18%, transparent);
  }

  @media (pointer: coarse) and (max-width: 768px) {
    .app-header {
      grid-template-columns: 1fr auto;
      grid-template-rows: auto auto;
      gap: var(--space-2);
    }
    .header-left {
      grid-column: 1;
      grid-row: 1;
    }
    .header-right {
      grid-column: 2;
      grid-row: 1;
      gap: var(--space-1);
    }
    /* Mobile: search spans full width on row 2. The segmented (Edit /
       Preview toggle) moves into the right cluster on row 1 — it's
       small enough to fit alongside the icons. */
    .search-wrap {
      grid-column: 1 / -1;
      grid-row: 2;
      max-width: 100%;
      justify-self: stretch;
    }
    .app-main {
      grid-template-columns: 1fr !important;
      padding: 0 var(--space-2) var(--space-2);
      gap: var(--space-2);
    }
    .editor-pane,
    .preview-pane {
      display: none;
    }
    .app[data-mode-active='write'] .editor-pane {
      display: block;
    }
    .app[data-mode-active='read'] .preview-pane {
      display: block;
    }
  }
</style>
